import { z } from 'zod';
import { MoneyStringSchema } from './money';

/**
 * 数字货币充值（docs/14）：仅海外站。方案 D 自建 HD 钱包，首期 USDT-TRC20、USDT-BEP20、USDC-Base。
 * 金额对外是 6 位小数字符串（USD 额度，稳定币 1:1）。
 */
export const CRYPTO_ASSETS = ['USDT', 'USDC'] as const;
export type CryptoAsset = (typeof CRYPTO_ASSETS)[number];

/** 链：首期 tron、bsc、base；ethereum、solana 以后按需开放 */
export const CRYPTO_CHAINS = ['tron', 'bsc', 'base', 'ethereum'] as const;
export type CryptoChain = (typeof CRYPTO_CHAINS)[number];

/** 地址派生族：EVM 链（bsc、base、ethereum）共用同一地址 */
export const chainFamily = (chain: CryptoChain): 'evm' | 'tron' => (chain === 'tron' ? 'tron' : 'evm');

export const DEPOSIT_STATUSES = [
  'detected',
  'confirming',
  'confirmed',
  'credited',
  'below_minimum',
  'unsupported_token',
  'risk_hold',
  'reorged',
  'rejected',
] as const;
export type DepositStatus = (typeof DEPOSIT_STATUSES)[number];

export const CLAIM_STATUSES = ['open', 'resolved', 'rejected'] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

const TxHashSchema = z.string().trim().min(10).max(128).regex(/^(0x)?[0-9a-fA-F]+$/, '交易哈希格式不正确');

// ───────────────────────── 用户端 ─────────────────────────

/** GET /portal/billing/crypto/networks */
export const PublicCryptoNetworkSchema = z.object({
  id: z.string(),
  asset: z.enum(CRYPTO_ASSETS),
  chain: z.enum(CRYPTO_CHAINS),
  /** 展示名，如 "TRC20" */
  network_label: z.string(),
  min_deposit: z.string(),
  confirmations: z.number().int(),
  /** 预计到账时间（分钟），用于展示 */
  eta_minutes: z.number().int(),
  /** 维护中：可以看地址，但页面提示暂停充值 */
  paused: z.boolean(),
});
export type PublicCryptoNetwork = z.infer<typeof PublicCryptoNetworkSchema>;

/** POST /portal/billing/crypto/address */
export const DepositAddressRequestSchema = z.object({ network_id: z.string().min(1) });
export const DepositAddressSchema = z.object({
  network_id: z.string(),
  asset: z.enum(CRYPTO_ASSETS),
  chain: z.enum(CRYPTO_CHAINS),
  address: z.string(),
  min_deposit: z.string(),
  confirmations: z.number().int(),
});
export type DepositAddress = z.infer<typeof DepositAddressSchema>;

export const CryptoDepositSchema = z.object({
  id: z.string(),
  asset: z.enum(CRYPTO_ASSETS),
  chain: z.enum(CRYPTO_CHAINS),
  network_label: z.string(),
  tx_hash: z.string(),
  /** 区块浏览器链接 */
  explorer_url: z.string().nullable(),
  amount: z.string(),
  bonus: z.string().nullable(),
  status: z.enum(DEPOSIT_STATUSES),
  confirmations: z.number().int(),
  required_confirmations: z.number().int(),
  detected_at: z.string(),
  credited_at: z.string().nullable(),
});
export type CryptoDeposit = z.infer<typeof CryptoDepositSchema>;

/** POST /portal/billing/crypto/claims："充值未到账"申诉 */
export const CreateDepositClaimSchema = z.object({
  network_id: z.string().min(1),
  tx_hash: TxHashSchema,
  note: z.string().trim().max(500).optional(),
});

/** GET /portal/billing/crypto/terms */
export const TopupTermsSchema = z.object({
  version: z.number().int(),
  /** 当前界面语言的正文（纯文本，按行分段） */
  body: z.string(),
  accepted: z.boolean(),
});
export type TopupTerms = z.infer<typeof TopupTermsSchema>;
/** POST /portal/billing/crypto/terms */
export const AcceptTopupTermsSchema = z.object({ version: z.number().int().min(1) });

/** GET /portal/billing/crypto/promotions：当前可参与的赠送活动 */
export const TopupPromotionSchema = z.object({
  id: z.string(),
  name: z.string(),
  asset: z.enum(CRYPTO_ASSETS).nullable(),
  chain: z.enum(CRYPTO_CHAINS).nullable(),
  min_amount: z.string(),
  max_amount: z.string().nullable(),
  bonus_type: z.enum(['percent', 'fixed']),
  /** percent：百分比字符串，如 "5.00"；fixed：额度字符串 */
  bonus_value: z.string(),
  max_bonus: z.string().nullable(),
  first_deposit_only: z.boolean(),
  ends_at: z.string().nullable(),
});
export type TopupPromotion = z.infer<typeof TopupPromotionSchema>;

// ───────────────────────── 管理后台 ─────────────────────────

export const AdminCryptoNetworkSchema = z.object({
  id: z.string(),
  asset: z.enum(CRYPTO_ASSETS),
  chain: z.enum(CRYPTO_CHAINS),
  network_label: z.string(),
  contract_address: z.string(),
  decimals: z.number().int(),
  confirmations: z.number().int(),
  min_deposit: z.string(),
  eta_minutes: z.number().int(),
  explorer_tx_url: z.string().nullable(),
  enabled: z.boolean(),
  deposit_paused: z.boolean(),
  display_order: z.number().int(),
});
export type AdminCryptoNetwork = z.infer<typeof AdminCryptoNetworkSchema>;

export const UpdateCryptoNetworkSchema = z
  .object({
    network_label: z.string().trim().min(1).max(32),
    contract_address: z.string().trim().min(10).max(64),
    decimals: z.number().int().min(0).max(30),
    confirmations: z.number().int().min(1).max(10_000),
    min_deposit: MoneyStringSchema,
    eta_minutes: z.number().int().min(0).max(24 * 60),
    explorer_tx_url: z.string().trim().max(200).nullable(),
    enabled: z.boolean(),
    deposit_paused: z.boolean(),
    display_order: z.number().int().min(0).max(1000),
  })
  .partial();

export const AdminCryptoDepositSchema = CryptoDepositSchema.extend({
  user_id: z.string(),
  user_email: z.string(),
  network_id: z.string(),
  log_index: z.number().int(),
  block_number: z.string().nullable(),
  from_address: z.string().nullable(),
  to_address: z.string(),
  amount_raw: z.string(),
  bonus_rule_id: z.string().nullable(),
  risk_note: z.string().nullable(),
  reviewed_by: z.string().nullable(),
  reviewed_at: z.string().nullable(),
  ledger_entry_id: z.string().nullable(),
});
export type AdminCryptoDeposit = z.infer<typeof AdminCryptoDepositSchema>;

/** 审核放行 / 人工入账：可选是否适用赠送（默认不适用） */
export const ApproveDepositSchema = z.object({ apply_bonus: z.boolean().default(false) });
export const ManualCreditDepositSchema = z.object({
  /** 实际入账额度（低于最小充值时可扣除归集成本后入账） */
  amount: MoneyStringSchema,
  apply_bonus: z.boolean().default(false),
});

export const AdminDepositClaimSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_email: z.string(),
  network_id: z.string(),
  network_label: z.string(),
  tx_hash: z.string(),
  note: z.string().nullable(),
  status: z.enum(CLAIM_STATUSES),
  resolution_note: z.string().nullable(),
  created_at: z.string(),
  resolved_at: z.string().nullable(),
});
export type AdminDepositClaim = z.infer<typeof AdminDepositClaimSchema>;
export const ResolveDepositClaimSchema = z.object({
  status: z.enum(['resolved', 'rejected']),
  resolution_note: z.string().trim().min(1).max(500),
});

/** 充值赠送规则（docs/14 §13） */
const BonusRuleFields = z.object({
  name: z.string().trim().min(1).max(64),
  enabled: z.boolean(),
  priority: z.number().int().min(0).max(1000),
  starts_at: z.iso.datetime().nullable(),
  ends_at: z.iso.datetime().nullable(),
  asset: z.enum(CRYPTO_ASSETS).nullable(),
  chain: z.enum(CRYPTO_CHAINS).nullable(),
  min_amount: MoneyStringSchema,
  max_amount: MoneyStringSchema.nullable(),
  bonus_type: z.enum(['percent', 'fixed']),
  /** percent：百分比（最多 2 位小数，0–100）；fixed：额度 */
  bonus_value: MoneyStringSchema,
  max_bonus: MoneyStringSchema.nullable(),
  first_deposit_only: z.boolean(),
  per_user_limit: z.number().int().min(1).nullable(),
  budget: MoneyStringSchema.nullable(),
});
export const CreateBonusRuleSchema = BonusRuleFields;
export const UpdateBonusRuleSchema = BonusRuleFields.partial();
export const AdminBonusRuleSchema = BonusRuleFields.extend({
  id: z.string(),
  used: z.string(),
  granted_count: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AdminBonusRule = z.infer<typeof AdminBonusRuleSchema>;

/** POST /admin/topup-bonus-rules/simulate */
export const SimulateBonusSchema = z.object({
  amount: MoneyStringSchema,
  network_id: z.string().min(1),
  user_id: z.string().nullable().optional(),
});
export const SimulateBonusResultSchema = z.object({
  rule_id: z.string().nullable(),
  rule_name: z.string().nullable(),
  bonus: z.string(),
});

/** GET/PUT /admin/settings/topup-terms：充值须知（按语言的正文 + 版本） */
export const TopupTermsSettingsSchema = z.object({
  version: z.number().int().min(1),
  /** 按语言的正文；缺失的语言回退到英文，再回退到简体中文 */
  bodies: z.record(z.string(), z.string().max(10_000)),
});
export type TopupTermsSettings = z.infer<typeof TopupTermsSettingsSchema>;
